import { useRef } from "react";
import { QRCode, Button } from "antd";

// Splits text into lines that fit maxWidth on the given canvas context.
function wrapText(ctx, text, maxWidth) {
  const words = text.split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";
  words.forEach((word) => {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  });
  if (line) lines.push(line);
  return lines;
}

// Renders a QR code encoding the item's SKU, with an optional PNG download
// that includes the product name and SKU beneath the code.
function ItemSkuQrCode({ sku, name, size = 160, showDownload = true }) {
  const wrapperRef = useRef(null);
  const value = sku == null ? "" : String(sku).trim();
  const productName = name == null ? "" : String(name).trim();

  if (!value) {
    return <div className="text-xs text-gray-400">No SKU — QR code unavailable</div>;
  }

  function downloadQr() {
    const qrCanvas = wrapperRef.current?.querySelector("canvas");
    if (!qrCanvas) return;

    const padding = 32;
    const qrSize = qrCanvas.width;
    const width = qrSize + padding * 2;
    const textMaxWidth = width - padding * 2;
    const nameFont = "bold 22px Arial, sans-serif";
    const skuFont = "16px Arial, sans-serif";
    const nameLineHeight = 28;
    const skuLineHeight = 22;

    // Measure wrapped name lines first so the canvas height fits them.
    const measure = document.createElement("canvas").getContext("2d");
    measure.font = nameFont;
    const nameLines = productName ? wrapText(measure, productName, textMaxWidth) : [];

    const height =
      padding + qrSize + 20 + nameLines.length * nameLineHeight + skuLineHeight + padding;

    const out = document.createElement("canvas");
    out.width = width;
    out.height = height;
    const ctx = out.getContext("2d");

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(qrCanvas, padding, padding, qrSize, qrSize);

    ctx.fillStyle = "#111827";
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    let y = padding + qrSize + 20;

    ctx.font = nameFont;
    nameLines.forEach((line) => {
      ctx.fillText(line, width / 2, y);
      y += nameLineHeight;
    });

    ctx.font = skuFont;
    ctx.fillStyle = "#4b5563";
    ctx.fillText(`SKU: ${value}`, width / 2, y);

    const safe = (s) => s.replace(/[^a-z0-9-_]+/gi, "_").replace(/^_+|_+$/g, "");
    const fileName = [safe(productName), safe(value)].filter(Boolean).join("_") || "item-qr";

    const a = document.createElement("a");
    a.href = out.toDataURL("image/png");
    a.download = `${fileName}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  return (
    <div ref={wrapperRef} className="flex flex-col items-center gap-2">
      <QRCode value={value} size={size} errorLevel="H" bgColor="#fff" />
      {productName && (
        <div className="max-w-[280px] text-center text-sm font-semibold text-gray-700">
          {productName}
        </div>
      )}
      <div className="text-xs text-gray-600">
        SKU: <b>{value}</b>
      </div>
      {showDownload && (
        <Button size="small" onClick={downloadQr}>
          Download PNG
        </Button>
      )}
    </div>
  );
}

export default ItemSkuQrCode;
